import { useLayoutEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { ConfigProvider } from 'antd';

import ErrorBoundary from '../components/ErrorBoundary';
import { queryClient } from '../queries/queryClient';
import { createAppRouter, type AppRouter } from '../routes/createAppRouter';
import { buildAntdTheme } from '../theme/antdTheme';
import { useThemeStore } from '../store/themeStore';

const defaultRouter = createAppRouter(queryClient);

type AppProps = {
  router?: AppRouter;
};

const App = ({ router = defaultRouter }: AppProps) => {
  const mode = useThemeStore((state) => state.mode);

  // Reflect the mode on <html> so native controls/scrollbars match and any custom
  // [data-theme='dark'] CSS hooks resolve; antd itself flips via the algorithm below.
  // useLayoutEffect (not useEffect) so it applies before first paint — otherwise a dark-mode
  // reload briefly paints the custom [data-theme] tokens (header border, DNP bars) with their
  // light defaults before flipping.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = mode;
    root.style.colorScheme = mode;
  }, [mode]);

  return (
    <ConfigProvider theme={buildAntdTheme(mode)}>
      <ErrorBoundary>
        <div data-testid="sa-app">
          <RouterProvider router={router} />
        </div>
      </ErrorBoundary>
    </ConfigProvider>
  );
};

export default App;
